/**
 * Image-embedding primitives (UForm cross-modal space, tickets 05/06). Pure
 * logic + a Zod schema shared by the browser (gallery image search) and the
 * server (image-embedding pipeline + search API), so a query ranks identically
 * everywhere — the same reason `query.ts` and `search-scoring.ts` live here.
 * No model runs in this module; the UForm encoder wrapper is feature-local.
 */
import { z } from "zod";
/** UForm's `uform3-image-text-multilingual-base` embedding width (ticket 02). */
export const EMBEDDING_DIM = 256;
/**
 * A stored image embedding (ticket 04): a 256-dim vector, L2-normalized at
 * write time so cosine similarity is a plain dot product, plus the model that
 * produced it and when it was computed.
 */
export const imageEmbeddingSchema = z.object({
    vector: z.array(z.number()).length(EMBEDDING_DIM),
    model: z.string(),
    at: z.string().datetime(),
});
/**
 * L2-normalize a vector to unit length, returning a new array (the input is
 * never mutated). A zero vector has no direction, so it normalizes to itself
 * (all zeros) and its cosine with any other vector is 0.
 */
export function normalizeVector(vector) {
    const norm = Math.sqrt(vector.reduce((sum, v) => sum + v * v, 0));
    if (norm === 0)
        return [...vector];
    return vector.map((v) => v / norm);
}
/**
 * Cosine similarity between two unit-normalized vectors — the dot product.
 * Callers normalize with {@link normalizeVector} first (at write and query
 * time); the result lies in [-1, 1] (1 identical, 0 orthogonal, -1 opposite).
 * Throws on a length mismatch rather than silently scoring a truncated pair.
 */
export function cosineSimilarity(a, b) {
    if (a.length !== b.length) {
        throw new Error(`Vector length mismatch: ${a.length} vs ${b.length}`);
    }
    let dot = 0;
    for (let i = 0; i < a.length; i++)
        dot += a[i] * b[i];
    return dot;
}
/**
 * Rank candidates by cosine similarity to a unit-normalized query vector,
 * mapping each score to [0, 1] and dropping those below `cutoff` (default 0.5).
 * The sort is deterministic (score desc, name asc, createdAt asc), so the
 * browser and the server return the same order for the same inputs.
 */
export function rankImageEmbeddings(query, candidates, options = {}) {
    const cutoff = options.cutoff ?? DEFAULT_CUTOFF;
    return candidates
        .map((candidate) => ({
        item: candidate,
        score: toUnitScore(cosineSimilarity(query, candidate.vector)),
    }))
        .filter((entry) => entry.score >= cutoff)
        .sort(compareRank);
}
// ── Private helpers ────────────────────────────────────────────────
/** Map a cosine in [-1, 1] to [0, 1]: 1 → 1, 0 → 0.5, -1 → 0 (ticket 03 Q6a). */
function toUnitScore(cosine) {
    return (cosine + 1) / 2;
}
/** Cutoff below which candidates are hidden (ticket 03 Q6a, tunable). */
const DEFAULT_CUTOFF = 0.5;
/**
 * Deterministic tiebreak: cosine desc, then name asc, then createdAt asc
 * (ticket 03 Q6a). Plain byte/locale-free comparison so browser and server
 * rank identically.
 */
function compareRank(a, b) {
    if (b.score !== a.score)
        return b.score - a.score;
    if (a.item.name !== b.item.name)
        return a.item.name < b.item.name ? -1 : 1;
    if (a.item.createdAt !== b.item.createdAt) {
        return a.item.createdAt < b.item.createdAt ? -1 : 1;
    }
    return 0;
}
//# sourceMappingURL=embedding.js.map